/*
 * Originally extracted from intake-modal.tsx (authored by Tony Ramirez).
 * The "elevated onboarding" shell — non-dismissible blurred-backdrop modal
 * with a 62/38 column split (content left, hero panel right) and a thin
 * product-header strip. State primitives (onboarding provider, step
 * transitions, etc.) live in consumer code; this component is chrome only.
 */
import RedoLogo from "@redotech/redo-icons/redo-logo-cutout.svg";
import classNames from "classnames";
import { ComponentType, memo, ReactNode } from "react";
import { Flex } from "../../flex";
import { RedoModal } from "../modal/redo-modal";
import { RedoProgressBar } from "../progress-bar/redo-progress-bar";
import * as css from "./redo-elevated-onboarding.module.css";

export interface RedoElevatedOnboardingProps {
  /**
   * Modal title (passed to the underlying `RedoModal` for a11y). Also rendered
   * as a visible label next to the product icon in the header strip when
   * `hideProductName` is not set.
   */
  productName: string;
  /**
   * Hide the visible product-name label in the header strip while still using
   * `productName` as the accessible modal title. Useful when the consumer wants
   * just the logo (e.g. first-install onboarding).
   */
  hideProductName?: boolean;
  /** Icon shown at the top-left of the content column. Defaults to the Redo logo cutout. */
  ProductIcon?: ComponentType<{ className?: string }>;
  /** When provided, renders a thin progress bar in the header strip. */
  progressPercent?: number;
  /** Right column — typically a `<RedoElevatedOnboardingHeroPanel>`. */
  rightPanel: ReactNode;
  /** Optional band rendered below the body — does not scroll with the body. */
  footer?: ReactNode;
  /**
   * Step body. Scrolls inside the content column by default; pass
   * `bodyScroll={false}` if the consumer wants to manage overflow itself.
   */
  children?: ReactNode;
  /**
   * When false (default), the consumer's body sits inside a scrollable flex
   * column. When true, overflow is left to the consumer.
   */
  bodyScroll?: boolean;
  /** Non-dismissible by default. When true, exposes a close button + escape/backdrop close. */
  dismissible?: boolean;
  /** Invoked only when `dismissible` is true. */
  onCloseRequested?: () => void;
  /** Optional className applied to the body wrapper. */
  bodyClassName?: string;
}

export const RedoElevatedOnboarding = memo(function RedoElevatedOnboarding({
  productName,
  hideProductName = false,
  ProductIcon = RedoLogo,
  progressPercent,
  rightPanel,
  footer,
  children,
  bodyScroll = true,
  dismissible = false,
  onCloseRequested,
  bodyClassName,
}: RedoElevatedOnboardingProps) {
  return (
    <RedoModal
      className={css.modal}
      contentScrollable
      hideCloseButton={!dismissible}
      hideFooter
      hideHeader
      isOpen
      mainContentClassName={css.modalMain}
      modalSize="xl"
      muiClassName={css.backdropBlur}
      noPadding
      onModalCloseRequested={
        dismissible ? () => onCloseRequested?.() : swallowClose
      }
      title={productName}
    >
      <Flex className={css.frame} dir="row">
        <div className={css.contentColumn}>
          <div className={css.productHeader}>
            <ProductIcon className={css.productHeaderIcon} />
            {!hideProductName && (
              <span className={css.productHeaderName}>{productName}</span>
            )}
            {progressPercent != null && (
              <div
                aria-label="Onboarding progress"
                aria-valuemax={100}
                aria-valuemin={0}
                aria-valuenow={progressPercent}
                className={css.progressIndicator}
                role="progressbar"
              >
                <RedoProgressBar
                  labelStyle="none"
                  percentage={progressPercent}
                  style="thin"
                />
              </div>
            )}
          </div>

          <div
            className={classNames(
              css.body,
              bodyScroll && css.bodyScroll,
              bodyClassName,
            )}
          >
            {children}
          </div>

          {footer && <div className={css.columnFooter}>{footer}</div>}
        </div>

        <div className={css.imageWrapper}>{rightPanel}</div>
      </Flex>
    </RedoModal>
  );
});

function swallowClose(): void {
  // Intentional no-op — non-dismissible.
}
